/**
 * Custom PrimeNG Theme Preset — "opentowork" editorial system
 *
 * Design language (1:1 z prototypem opentowork.me):
 * - Akcent: LIMONKA (#d4ff3a) — używana WYŁĄCZNIE jako wypełnienie (primary button,
 *   aktywny element nawigacji w ciemnym sidebarze, awatary). NIGDY jako tekst na jasnym tle.
 * - Primary (semantic) = INK (#14171a) — czytelny dla tekstu/linków/ikon/focus.
 *   Dzięki temu ~116 użyć `--p-primary-color` jako tekstu pozostaje legible.
 * - Powierzchnie: ciepła szarość (warm-stone) zamiast zimnego slate.
 * - Typografia: Space Grotesk (UI/nagłówki) + JetBrains Mono (kody, dane, etykiety).
 *
 * Dark Mode: sterowany klasą '.app-dark' na <html>.
 */

import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

// Akcent limonkowy — pełna rampa (używana do subtelnych tłów / highlightów)
const lime = {
  50: '#f7ffe0',
  100: '#ecffb0',
  200: '#e0ff80',
  300: '#d4ff3a',
  400: '#c7f520',
  500: '#b2e000',
  600: '#8fb300',
  700: '#6b8600',
  800: '#4f6310',
  900: '#3f4d14',
  950: '#212b03',
};

// Ink button (lime fill + dark text) — wspólne dla light/dark, niezależne od primary.color
const limeButton = {
  primary: {
    background: lime[300],
    hoverBackground: lime[400],
    activeBackground: lime[500],
    borderColor: lime[300],
    hoverBorderColor: lime[400],
    activeBorderColor: lime[500],
    color: '#14171a',
    hoverColor: '#14171a',
    activeColor: '#14171a',
    focusRing: {
      color: '#14171a',
      shadow: 'none',
    },
  },
};

const MyPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '6px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '20px',
    },
  },
  semantic: {
    // Rampa primary = limonka (zasila subtelne highlighty/selekcje jako blade tło)
    primary: {
      50: lime[50],
      100: lime[100],
      200: lime[200],
      300: lime[300],
      400: lime[400],
      500: lime[500],
      600: lime[600],
      700: lime[700],
      800: lime[800],
      900: lime[900],
      950: lime[950],
    },
    colorScheme: {
      light: {
        // primary jako KOLOR (tekst/linki/ikony/focus) = INK — czytelny na jasnym tle
        primary: {
          color: '#14171a',
          inverseColor: '#ffffff',
          hoverColor: '#000000',
          activeColor: '#000000',
        },
        // Warm-stone — ciepła szarość zamiast zimnego slate
        surface: {
          0: '#ffffff',
          50: '#faf9f5',
          100: '#f3f3ef',
          200: '#e7e7e1',
          300: '#d8d8d1',
          400: '#b7b8af',
          500: '#9aa097',
          600: '#61665f',
          700: '#3c403a',
          800: '#262a25',
          900: '#14171a',
          950: '#0c0e10',
        },
      },
      dark: {
        // Na ciemnym tle limonka jest czytelna jako kolor — można jej użyć dla primary
        primary: {
          color: lime[300],
          inverseColor: '#14171a',
          hoverColor: lime[200],
          activeColor: lime[100],
        },
        surface: {
          0: '#ffffff',
          50: '#f5f5f1',
          100: '#23272b',
          200: '#262b30',
          300: '#2e333a',
          400: '#3b4148',
          500: '#7e857c',
          600: '#aab0a8',
          700: '#c7ccc3',
          800: '#e3e6df',
          900: '#f3f3ef',
          950: '#faf9f5',
        },
      },
    },
  },
  components: {
    card: {
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            // Płaska, edytorska karta — cień tylko subtelny, bez glassu
            shadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
          },
        },
      },
    },
    button: {
      root: {
        borderRadius: '10px',
      },
      colorScheme: {
        light: { root: limeButton },
        dark: { root: limeButton },
      },
    },
    inputtext: {
      root: {
        borderRadius: '10px',
      },
    },
    select: {
      root: {
        borderRadius: '10px',
      },
    },
    textarea: {
      root: {
        borderRadius: '10px',
      },
    },
    tag: {
      root: {
        borderRadius: '8px',
      },
    },
    chip: {
      root: {
        borderRadius: '8px',
      },
    },
  },
});

export default MyPreset;
